import {calculateGPFromPrice} from '../../../shared/shared-utils/calculations/calculations-stock';
import {IStore} from '../../../shared/shared-models/store/store';
import {VatRates} from '../../../shared-utilities/models-old/datastructures';
import {IQuoteStockItem} from '../models/quoteStockItems';
import {getQuoteStockItemVatRate} from './quote-vat.utils';

export interface IQuoteGpChangeDisplay {
  currentGp: number | null;
  delta: number;
  hasChange: boolean;
  originalGp: number | null;
}

/**
 * Multiplier applied to unit sell price from item discount percentage.
 */
export function getQuoteDiscountMultiplier(itemDiscount?: number): number {
  const discountPct: number = Number(itemDiscount ?? 0);
  if (!Number.isFinite(discountPct)) {
    return 1;
  }
  return 1 - discountPct / 100;
}

/**
 * Unit sell price after item discount percentage is applied.
 */
export function getQuoteEffectiveSellPriceForUnit(unitSellPrice: number, itemDiscount?: number): number {
  return unitSellPrice * getQuoteDiscountMultiplier(itemDiscount);
}

/**
 * Calculates nominal GP using the discounted effective sell price.
 */
export function calculateQuoteNominalGp(
  item: IQuoteStockItem,
  store?: IStore,
  vatRates?: VatRates,
  unitSellPrice?: number,
): number | null {
  if (!store || getQuoteStockItemVatRate(item, store, vatRates) === undefined) {
    const storedGp: number = Number(item.nominalGP);
    return Number.isFinite(storedGp) ? storedGp : null;
  }

  const unitSell: number = unitSellPrice ?? Number(item.sellPriIncl1 ?? 0);
  const effectiveSell: number = getQuoteEffectiveSellPriceForUnit(unitSell, item.itemDiscount);
  const result = calculateGPFromPrice(
    {...item, sellPriIncl1: effectiveSell},
    store,
    2,
    vatRates,
  );
  const gp: number = Number(result.nominalGP);
  return Number.isFinite(gp) ? gp : null;
}

/**
 * Builds store update fields including recalculated nominal GP after price or discount changes.
 */
export function resolveQuoteNominalGpUpdate(
  item: IQuoteStockItem,
  store: IStore | undefined,
  vatRates: VatRates | undefined,
  overrides: Partial<Pick<IQuoteStockItem, 'sellPriIncl1' | 'itemDiscount'>>,
): Partial<IQuoteStockItem> {
  const mergedItem: IQuoteStockItem = {...item, ...overrides};
  const gp: number | null = calculateQuoteNominalGp(mergedItem, store, vatRates);
  const changes: Partial<IQuoteStockItem> = {...overrides};

  if (gp != null) {
    changes.nominalGP = gp;
  }

  return changes;
}

/**
 * Whether the retail price differs from the original captured price.
 */
export function hasQuoteRetailPriceChanged(item: IQuoteStockItem): boolean {
  const currentPrice: number = Number(item.sellPriIncl1 ?? 0);
  const originalPrice: number = Number(item.originalSellPriIncl1 ?? item.sellPriIncl1 ?? 0);

  return !!item?.code
    && Number.isFinite(currentPrice)
    && Number.isFinite(originalPrice)
    && currentPrice !== originalPrice;
}

/**
 * Formats a GP percentage for display.
 */
export function formatQuoteGpPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  return `${value.toFixed(2)}%`;
}

/**
 * Formats a signed GP delta for display.
 */
export function formatQuoteGpDelta(delta: number): string {
  const sign: string = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(2)}%`;
}

/**
 * Derives current GP and delta from original retail price when price has changed.
 * GP values include item discount on both current and original prices.
 */
export function getQuoteGpChangeDisplay(
  item: IQuoteStockItem,
  store?: IStore,
  vatRates?: VatRates,
): IQuoteGpChangeDisplay {
  const originalPrice: number = Number(item.originalSellPriIncl1 ?? item.sellPriIncl1 ?? 0);
  const priceChanged: boolean = hasQuoteRetailPriceChanged(item);

  const currentGp: number | null = calculateQuoteNominalGp(item, store, vatRates);
  const originalGp: number | null = store && priceChanged
    ? calculateQuoteNominalGp(item, store, vatRates, originalPrice)
    : null;

  const hasChange: boolean =
    priceChanged &&
    currentGp != null &&
    originalGp != null &&
    Math.abs(currentGp - originalGp) >= 0.005;

  const delta: number = hasChange && currentGp != null && originalGp != null
    ? currentGp - originalGp
    : 0;

  return {
    currentGp,
    delta,
    hasChange,
    originalGp,
  };
}
